import type { CSSProperties } from 'react'
import { useHitTarget } from '../../platform/responsive/hitTarget'
import type { Txn } from './transactionWire'

/**
 * Whether a row carries a merchant pill: only a posted row with **no merchant entity**.
 *
 * - A row with a merchant already shows that merchant's name — a pill would say it twice. Changing an
 *   assigned merchant is done on the transaction page or with bulk Set merchant.
 * - A pending row gets none: only its category is editable until the bank posts it (Q5). EditPanel's
 *   merchant field is disabled on one, and bulk Set merchant skips them; the row's "Pending" chip says why.
 */
export function needsMerchantPill(t: Txn): boolean {
  return t.merchantId === null && !t.isPending
}

/**
 * "Needs merchant": a row with no merchant, as a control that opens the merchant picker for that row —
 * `CategoryPill`'s "Needs category", for merchants. Shared by the grid (under the row's name, in the
 * Merchant cell) and the phone cards (beside the category pill), so both name and wire it identically.
 * Renders nothing on a row `needsMerchantPill` excludes.
 *
 * A dashed chip in the warn token, in words as well as colour. The hit area is `CategoryPill`'s: the
 * `useHitTarget('page')` floor reaches CSS as `--pill-hit`, and the shared `::after` in `transactions.css`
 * reserves it around the chip without growing the visual.
 */
export function MerchantPill({ t, onMerchantClick }: { t: Txn; onMerchantClick: (t: Txn, opener: HTMLButtonElement) => void }) {
  const hit = useHitTarget('page')
  if (!needsMerchantPill(t)) return null
  return (
    <button
      type="button"
      className="txn-chip txn-chip--button txn-chip--needs-merchant"
      aria-haspopup="dialog"
      aria-label={`Merchant for ${t.merchant}: Needs merchant`}
      style={{ '--pill-hit': `${hit}px` } as CSSProperties}
      onClick={(e) => onMerchantClick(t, e.currentTarget)}
    >
      Needs merchant
      <span className="txn-pill-button__caret" aria-hidden="true">
        ▾
      </span>
    </button>
  )
}
